import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Crown, Star, ChevronLeft, ChevronRight, ShieldCheck, Package } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RATING_THRESHOLD = 4.5; // Show sellers with avg_rating >= 4.5

/**
 * Homepage "Vânzători Top" widget.
 *
 * Fetches top-rated sellers (>= 4.5⭐) from /api/sellers/top, renders a horizontal
 * scroll-snap carousel with arrows on desktop. Auto-hides when no qualified sellers.
 */
export default function TopSellersCarousel() {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/sellers/top`, {
          params: { sort: "rating", limit: 12 },
        });
        const qualified = (res.data?.sellers || []).filter(
          (s) => Number(s.avg_rating || 0) >= RATING_THRESHOLD
        );
        setSellers(qualified);
      } catch (e) {
        // Silent fail — widget simply won't render
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const scrollBy = (direction) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector("[data-seller-card]");
    const delta = card ? card.clientWidth + 16 : 280;
    el.scrollBy({ left: direction * delta * 2, behavior: "smooth" });
  };

  if (loading) return null;
  if (!sellers.length) return null;

  return (
    <section
      className="px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16 relative"
      data-testid="top-sellers-section"
    >
      {/* Subtle amber glow background */}
      <div className="absolute inset-0 bg-gradient-to-r from-amber-500/[0.03] via-transparent to-fuchsia-500/[0.03] pointer-events-none" />

      <div className="max-w-7xl mx-auto relative">
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/10 flex items-center justify-center ring-1 ring-amber-500/30">
              <Crown className="w-5 h-5 sm:w-6 sm:h-6 text-amber-400" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
                Vânzători Top
                <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[10px] font-bold uppercase tracking-wider">
                  4.5+
                </span>
              </h2>
              <p className="text-slate-400 text-xs sm:text-sm">
                Cei mai apreciați vânzători din comunitate
              </p>
            </div>
          </div>

          {/* Desktop arrows */}
          <div className="hidden md:flex items-center gap-2">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white flex items-center justify-center transition-colors"
              aria-label="Derulează stânga"
              data-testid="top-sellers-prev-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-white flex items-center justify-center transition-colors"
              aria-label="Derulează dreapta"
              data-testid="top-sellers-next-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-3 sm:gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth scrollbar-none"
          style={{ scrollbarWidth: "none" }}
          data-testid="top-sellers-scroller"
        >
          {sellers.map((s) => (
            <SellerCard key={s.user_id} seller={s} />
          ))}
        </div>
      </div>
    </section>
  );
}

function SellerCard({ seller }) {
  const rating = Number(seller.avg_rating || 0).toFixed(1);
  return (
    <Link
      to={`/seller/${seller.user_id}`}
      data-seller-card
      data-testid={`top-seller-card-${seller.user_id}`}
      className="group snap-start shrink-0 w-[220px] sm:w-[240px] rounded-2xl bg-gradient-to-br from-[#0f0f10] to-[#0a0a0a] border border-white/[0.07] hover:border-amber-500/40 p-5 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_8px_30px_rgba(251,191,36,0.1)]"
    >
      <div className="flex flex-col items-center text-center">
        <div className="relative mb-3">
          {seller.picture ? (
            <img
              src={seller.picture}
              alt=""
              className={`w-16 h-16 rounded-full object-cover ring-2 ring-offset-2 ring-offset-[#0a0a0a] ${seller.verified ? "ring-amber-500/60" : "ring-white/10"}`}
            />
          ) : (
            <div className={`w-16 h-16 rounded-full bg-gradient-to-br from-amber-500/30 to-fuchsia-600/30 flex items-center justify-center ring-2 ring-offset-2 ring-offset-[#0a0a0a] text-white font-bold text-xl ${seller.verified ? "ring-amber-500/60" : "ring-white/10"}`}>
              {(seller.name || "?").charAt(0).toUpperCase()}
            </div>
          )}
          {seller.verified && (
            <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-emerald-500 border-2 border-[#0a0a0a] flex items-center justify-center shadow-lg shadow-emerald-500/40">
              <ShieldCheck className="w-3 h-3 text-white" strokeWidth={3} />
            </span>
          )}
        </div>

        <div className="text-white font-semibold text-sm truncate w-full group-hover:text-amber-300 transition-colors">
          {seller.name || "Anonim"}
        </div>

        <div className="mt-2 flex items-center gap-1 text-amber-400 font-bold text-sm">
          <Star className="w-4 h-4 fill-amber-400" />
          <span>{rating}</span>
          <span className="text-slate-500 font-normal text-xs">
            ({seller.total_reviews || 0})
          </span>
        </div>

        <div className="mt-3 pt-3 border-t border-white/5 w-full flex items-center justify-center gap-1.5 text-slate-400 text-xs">
          <Package className="w-3.5 h-3.5" />
          <span>{seller.active_ads || 0} anunțuri active</span>
        </div>
      </div>
    </Link>
  );
}
